import Vue from 'vue';
import { fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const MIN_COL_WIDTH = 80;
const DEFAULT_COL_WIDTH = 150;

Vue.directive('columns-resizable', {
    inserted(el, binding) {
        const nodeName = el.firstElementChild?.nodeName;
        if (!['TABLE', 'THEAD'].includes(nodeName)) return;

        const table = nodeName === 'TABLE' ? el : el.parentElement;
        const thead = table.querySelector('thead');
        const ths = thead.querySelectorAll('th');

        const isObject = binding.value && typeof binding.value === 'object';
        const localStorageKey = isObject ? binding.value.key : (binding.value || null);
        const defaultColWidth = isObject && binding.value.defaultColWidth != null
            ? binding.value.defaultColWidth
            : DEFAULT_COL_WIDTH;
        const minColWidth = isObject && binding.value.minColWidth != null
            ? binding.value.minColWidth
            : MIN_COL_WIDTH;
        const resizable = isObject && binding.value.resizable != null
            ? binding.value.resizable
            : true;

        if (!localStorageKey) return;

        const hasCheckboxFirstColumn = thead.classList.contains('first-column-check');
        const isActionsColumn = (th) => th.classList.contains('st-actions-column');

        if (!resizable) {
            table.style.tableLayout = 'fixed';
            ths.forEach((th, index) => {
                if (hasCheckboxFirstColumn && index === 0) return;
                if (isActionsColumn(th)) return;
                th.style.width = `${defaultColWidth}px`;
            });
            const totalWidth = Array.from(ths).reduce((sum, th) => sum + (parseInt(th.style.width) || defaultColWidth), 0);
            table.style.width = `${totalWidth}px`;
            return;
        }

        const resizeContainer = document.createElement('div');
        table.style.position = 'relative';
        table.parentElement.style.position = 'relative';
        resizeContainer.style.position = 'absolute';
        resizeContainer.style.width = `${table.scrollWidth}px`;
        resizeContainer.style.zIndex = 100;
        resizeContainer.style.pointerEvents = 'none';
        resizeContainer.className = 'vue-columns-resizable';
        table.parentElement.insertBefore(resizeContainer, table);

        let moving = false;
        let movingIndex = 0;

        const loadWidthsFromLocalStorage = () => {
            let savedWidths;
            try {
                savedWidths = JSON.parse(localStorage.getItem(localStorageKey));
            } catch {
                savedWidths = null;
            }

            if (savedWidths && savedWidths.length === ths.length) {
                ths.forEach((th, index) => {
                    if (hasCheckboxFirstColumn && index === 0) return;
                    if (isActionsColumn(th)) return;
                    th.style.width = `${savedWidths[index]}px`;
                });
                return;
            }
            ths.forEach((th, index) => {
                if (hasCheckboxFirstColumn && index === 0) return;
                if (isActionsColumn(th)) return;
                th.style.width = `${defaultColWidth}px`;
            });
        };

        const saveWidthsToLocalStorage = () => {
            if (!localStorageKey) return;
            const widths = Array.from(ths).map(th => th.offsetWidth);
            localStorage.setItem(localStorageKey, JSON.stringify(widths));
        };

        loadWidthsFromLocalStorage();
        table.style.tableLayout = 'fixed';

        const bars = () => resizeContainer.querySelectorAll('.columns-resize-bar');

        const updateTableWidth = () => {
            const totalWidth = Array.from(ths).reduce((sum, th) => {
                const styled = parseInt(th.style.width, 10);
                return sum + (Number.isFinite(styled) ? styled : th.offsetWidth);
            }, 0);
            table.style.width = `${totalWidth}px`;
            resizeContainer.style.width = `${totalWidth}px`;
        };

        const getBarLeft = (nextTh, scrollLeft) => {
            if (isActionsColumn(nextTh) && !moving) {
                const tableRect = table.getBoundingClientRect();
                return nextTh.getBoundingClientRect().left - tableRect.left - 16;
            }
            return nextTh.offsetLeft - 16 - scrollLeft;
        };

        const updateBarsPositions = () => {
            const scrollLeft = table.scrollLeft;
            bars().forEach((bar) => {
                const thIndex = parseInt(bar.dataset.thIndex, 10);
                const nextTh = ths[thIndex + 1];
                bar.style.left = `${getBarLeft(nextTh, scrollLeft)}px`;
                bar.style.height = `${table.offsetHeight}px`;
            });
            resizeContainer.style.width = `${table.scrollWidth}px`;
        };

        const createBars = () => {
            resizeContainer.innerHTML = '';
            ths.forEach((th, index) => {
                if (!(hasCheckboxFirstColumn && index === 0) && !isActionsColumn(th)) {
                    th.style.width = `${th.offsetWidth}px`;
                }

                if (index + 1 >= ths.length) return;
                if (hasCheckboxFirstColumn && index === 0) return;

                const nextTh = ths[index + 1];
                const bar = document.createElement('div');
                bar.dataset.thIndex = index;

                bar.style.position = 'absolute';
                bar.style.left = `${getBarLeft(nextTh, table.scrollLeft)}px`;
                bar.style.top = '0';
                bar.style.height = `${table.offsetHeight}px`;
                bar.style.width = '32px';
                bar.style.cursor = 'col-resize';
                bar.style.zIndex = 10;
                bar.style.pointerEvents = 'auto';
                bar.className = 'columns-resize-bar';

                const indicator = document.createElement('div');
                indicator.style.position = 'absolute';
                indicator.style.left = '16px';
                indicator.style.top = '0';
                indicator.style.width = '1px';
                indicator.style.height = '100%';
                indicator.style.pointerEvents = 'none';
                bar.appendChild(indicator);

                bar.addEventListener('mouseenter', () => {
                    indicator.style.backgroundColor = '#E7EDF3';
                });
                bar.addEventListener('mouseleave', () => {
                    if (!moving) indicator.style.backgroundColor = '';
                });

                bar.addEventListener('mousedown', () => {
                    moving = true;
                    movingIndex = index;
                    document.body.style.cursor = 'col-resize';
                    document.body.style.userSelect = 'none';
                });

                resizeContainer.appendChild(bar);
            });
            updateTableWidth();
        };

        createBars();

        const mouseUpHandler = () => {
            if (!moving) return;
            moving = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            bars().forEach((bar) => {
                const thIndex = parseInt(bar.dataset.thIndex, 10);
                const th = ths[thIndex];
                th.style.width = `${th.offsetWidth}px`;
                if (bar.firstElementChild) {
                    bar.firstElementChild.style.backgroundColor = '';
                }
            });

            saveWidthsToLocalStorage();
            updateTableWidth();
            updateBarsPositions();
        };
        document.addEventListener('mouseup', mouseUpHandler);
        el.__mouseUpHandler__ = mouseUpHandler;

        const handleResize = (e) => {
            if (!moving) return;

            const th = ths[movingIndex];

            const newThWidth = parseInt(th.style.width) + e.movementX;

            if (newThWidth < minColWidth) return;

            th.style.width = `${newThWidth}px`;

            updateTableWidth();
            updateBarsPositions();
        };

        resizeContainer.addEventListener('mousemove', handleResize);
        table.addEventListener('mousemove', handleResize);

        const recalculateBars = () => {
            if (table.clientWidth === 0) return;
            createBars();
        };

        const scrollSub = fromEvent(table.parentElement, 'scroll')
            .subscribe(() => updateBarsPositions());

        const windowResizeSub = fromEvent(window, 'resize')
            .pipe(debounceTime(400))
            .subscribe(() => {
                recalculateBars();
                updateBarsPositions();
            });

        const tbody = table.querySelector('tbody');
        const mutationObserver = new MutationObserver(() => {
            updateBarsPositions();
        });
        if (tbody) {
            mutationObserver.observe(tbody, { childList: true });
        }
        el.__mutationObserver__ = mutationObserver;

        el.__resizeSubscriptions__ = [scrollSub, windowResizeSub];
    },

    unbind(el) {
        if (el.__mouseUpHandler__) {
            document.removeEventListener('mouseup', el.__mouseUpHandler__);
            delete el.__mouseUpHandler__;
        }

        if (el.__resizeSubscriptions__) {
            el.__resizeSubscriptions__.forEach(sub => sub.unsubscribe());
            delete el.__resizeSubscriptions__;
        }

        if (el.__mutationObserver__) {
            el.__mutationObserver__.disconnect();
            delete el.__mutationObserver__;
        }
    }
});
